import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Badge, Button, Drawer, Empty, Modal, Space, Tag, Typography, message } from "antd";
import {
  CloudOutlined,
  CloudSyncOutlined,
  DisconnectOutlined,
  ExclamationCircleOutlined,
  SyncOutlined
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { logoutAndRedirect } from "../commons/Auth/auth";
import { useIsMobile } from "../design-system/useMediaQuery";
import { STATES, discardItem, retryItem, syncNow, useOfflineState } from "./index";
import "./offline.css";

const MODULES = {
  "work-reports": { name: "Work report", path: id => `/user/work-reports/${id}` },
  measurements: { name: "Measurement", path: id => `/user/measurements/${id}` },
  "site-docs": { name: "Site documentation", path: id => `/user/site-docs/${id}` },
  attendance: { name: "Time recording", path: () => "/user/attendance" }
};
const KINDS = { create: "New", update: "Changes", photo: "Photo", clock: "Clock" };

const STATE_TAGS = {
  [STATES.PENDING]: { color: "processing", label: "Waiting" },
  [STATES.AUTH]: { color: "warning", label: "Sign in again" },
  [STATES.CONFLICT]: { color: "error", label: "Conflict" },
  [STATES.ERROR]: { color: "error", label: "Failed" }
};

const PendingItem = ({ item, onNavigate }) => {
  const { t } = useTranslation("dashboard");
  const module = MODULES[item.module] || { name: item.module, path: () => null };
  const recordId = item.target || item.localId;
  const path = module.path(recordId);
  const tag = STATE_TAGS[item.state] || STATE_TAGS[STATES.PENDING];
  const canOverwrite = item.state === STATES.CONFLICT && item.error?.code === "CONFLICT";
  const canRetry =
    [STATES.ERROR, STATES.AUTH].includes(item.state) ||
    (item.state === STATES.CONFLICT && !canOverwrite);

  const discard = () =>
    Modal.confirm({
      title: t("Discard this change?"),
      content:
        item.kind === "create"
          ? t("The record and everything added to it offline are deleted from this device.")
          : t("The change is deleted from this device and will not be uploaded."),
      okText: t("Discard"),
      okButtonProps: { danger: true },
      cancelText: t("Cancel"),
      onOk: () => discardItem(item.id)
    });

  const overwrite = () =>
    Modal.confirm({
      title: t("Overwrite the version on the server?"),
      content: t(
        "Someone changed this record while you were offline. Their changes will be replaced by yours."
      ),
      okText: t("Overwrite"),
      okButtonProps: { danger: true },
      cancelText: t("Cancel"),
      onOk: () => retryItem(item.id, { overwrite: true })
    });

  return (
    <li className="offline-sheet__item">
      <div className="offline-sheet__item-head">
        <div className="offline-sheet__item-title">
          <strong>{t(module.name)}</strong>
          <span className="offline-sheet__item-sub">
            {item.label || t(KINDS[item.kind] || item.kind)} ·{" "}
            {dayjs(item.createdAt).format("DD.MM. HH:mm")}
          </span>
        </div>
        <Tag color={tag.color}>{t(tag.label)}</Tag>
      </div>
      {item.error && !item.error.network && item.error.message && (
        <Typography.Paragraph
          type={item.state === STATES.PENDING ? "secondary" : "danger"}
          className="offline-sheet__error"
        >
          {t(item.error.message)}
        </Typography.Paragraph>
      )}
      <Space wrap size={[8, 8]}>
        {path && (
          <Link to={path} onClick={onNavigate}>
            <Button size="small">{t("Open")}</Button>
          </Link>
        )}
        {canRetry && (
          <Button size="small" onClick={() => retryItem(item.id)}>
            {t("Retry")}
          </Button>
        )}
        {canOverwrite && (
          <Button size="small" onClick={overwrite}>
            {t("Keep my version")}
          </Button>
        )}
        <Button size="small" danger onClick={discard}>
          {t("Discard")}
        </Button>
      </Space>
    </li>
  );
};

/** Online/offline state in the top bar; opens the list of changes waiting to be uploaded. */
const OfflineIndicator = () => {
  const { t } = useTranslation("dashboard");
  const isMobile = useIsMobile();
  const state = useOfflineState();
  const [open, setOpen] = useState(false);
  const online = state.online && state.reachable;
  const count = state.items.length;
  const problems = state.items.filter(item => item.state !== STATES.PENDING);
  const needsLogin = state.items.some(item => item.state === STATES.AUTH);

  // Tell the user once per change of connectivity, not on every render
  const previous = useRef(online);
  useEffect(() => {
    if (previous.current === online) return;
    previous.current = online;
    if (!online) {
      message.warning({
        key: "offline-state",
        content: t("You are offline. Changes are saved on this device and uploaded later.")
      });
    } else if (count) {
      message.info({
        key: "offline-state",
        content: t("Back online. Uploading {{count}} changes …", { count })
      });
    } else {
      message.success({ key: "offline-state", content: t("Back online.") });
    }
  }, [online]);

  const icon = !online ? (
    <DisconnectOutlined />
  ) : state.syncing ? (
    <SyncOutlined spin />
  ) : count ? (
    <CloudSyncOutlined />
  ) : (
    <CloudOutlined />
  );
  const title = !online
    ? t("Offline")
    : count
      ? t("{{count}} changes not uploaded yet", { count })
      : t("Online – everything is uploaded");

  return (
    <>
      <Badge count={count} size="small" color={problems.length ? "red" : "orange"} offset={[-6, 6]}>
        <Button
          type="text"
          className={`app-topbar__icon-btn offline-indicator${online ? "" : " offline-indicator--offline"}`}
          icon={icon}
          onClick={() => setOpen(true)}
          aria-label={title}
          title={title}
        />
      </Badge>
      <Drawer
        rootClassName="offline-sheet"
        open={open}
        onClose={() => setOpen(false)}
        placement={isMobile ? "bottom" : "right"}
        height={isMobile ? "auto" : undefined}
        width={isMobile ? undefined : 440}
        title={t("Changes on this device")}
        footer={
          <Button
            type="primary"
            block
            icon={<SyncOutlined spin={state.syncing} />}
            disabled={!count || !state.online || state.syncing}
            onClick={() => syncNow({ force: true })}
          >
            {t("Sync now")}
          </Button>
        }
      >
        <div className="offline-sheet__status">
          {online ? (
            <Alert
              type={count ? "info" : "success"}
              showIcon
              message={
                count
                  ? t("Online. Waiting changes are uploaded automatically.")
                  : t("Online – everything is uploaded")
              }
              description={
                state.lastSyncAt
                  ? t("Last sync: {{time}}", { time: dayjs(state.lastSyncAt).format("HH:mm") })
                  : undefined
              }
            />
          ) : (
            <Alert
              type="warning"
              showIcon
              icon={<DisconnectOutlined />}
              message={t("Offline")}
              description={t(
                "Work reports, measurements, site photos and clock times are saved on this device and uploaded as soon as there is a connection."
              )}
            />
          )}
          {needsLogin && (
            <Alert
              type="warning"
              showIcon
              icon={<ExclamationCircleOutlined />}
              message={t("Your session has expired")}
              description={t("Sign in again to upload your changes. They stay on this device.")}
              action={
                <Button size="small" onClick={logoutAndRedirect}>
                  {t("Sign in")}
                </Button>
              }
            />
          )}
        </div>
        {count ? (
          <ul className="offline-sheet__list">
            {state.items.map(item => (
              <PendingItem key={item.id} item={item} onNavigate={() => setOpen(false)} />
            ))}
          </ul>
        ) : (
          <Empty description={t("No changes waiting")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Drawer>
    </>
  );
};

export default OfflineIndicator;
